import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import liff from "@line/liff";
import api, { handleRequest } from "@services/axios/apiManager";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { AxiosResponse, AxiosError } from "axios";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { getRuntimeLiffId } from "@utils/line/getLiffId";
import { useUser } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useNavigate } from "react-router-dom";
import { hideBootSplash, holdBootSplash, markBootRedirecting, releaseBootSplash } from "@utils/bootSplash";
import { captureFamilyInviteFromUrl, getFamilyInviteCode } from "@utils/familyInvite";
import { setPayReturnTo } from "@utils/payReturnTo";
import { captureShopInviteFromUrl, clearShopInviteCode, getShopEntrySupplierId, getShopInviteCode } from "@utils/shopInviteCode";
import { captureReferredCourierFromUrl, clearReferredCourierId } from "@utils/referredCourier";
import { resolveShopEntryRedirect, shouldPreserveQueryOnLogin } from "@utils/line/unregisteredRedirect";
import { resolveSerialLinkRegisteredRedirect } from "@utils/line/serialLinkRegisteredEntry";
import { capturedBootLocation, capturedLiffStateAtBoot, looksLikeLiffOauthCallbackBoot, resolveEffectiveLocation, revokeLoginJourney, stampLoginJourney } from "@utils/line/liffStateDeepLink";
import { flush, track } from "@lib/tracker";
import { OA_ENTRY_PREFERRED_CONTEXT_KEY, parseOaEntryFromSearch, resolveEntryCheckAction } from "@utils/line/oaEntryCheck";
import { captureSmsInviteFromUrl } from "@utils/smsInvite";
import { isPracticeMode, PRACTICE_FAKE_JWT, PRACTICE_FAKE_LINE_TOKEN } from "@utils/practice/practiceMode";
import { ensurePracticeIdentity } from "@utils/practice/practiceIdentity";
import { isPayLinkMode } from "@utils/payLink";
import { THREE_DS_RESULT_PATH } from "@utils/payThreeDsReturn";
import { isSmsInviteOpenMode } from "@utils/smsInviteOpen";
import { isIPassEntryRelayMode } from "@utils/ipassEntryRelay";
import { isSmsShortLinkMode } from "@utils/smsShortLink";
import { entryCheck, EntryCheckData } from "@screen/Onboarding/registerApi";


export const AuthReadyContext = createContext(false);
export const useAuthReady = () => useContext(AuthReadyContext);

const isDevSkipLiff = process.env.NODE_ENV === "development" && process.env.REACT_APP_SKIP_LIFF_LOGIN === "true";
const LineLoginProvider = ({ children }: Readonly<{ children: React.ReactNode }>) => {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const { setLineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const { setCusJwtToken } = useCusAuth();
  const isInitialized = useRef(false);

  const setAuthReadyTrue = () => setAuthReady(true);

  /** 深連結診斷事件（2026-09-03 使用者要求：前端 log 不可只留 console）——
   *  走既有 tracking 管線落地 S3，後台直接可查。記錄開機原始網址、開機抄存的
   *  liff.state、判斷當下網址、解析出的有效目的地與最終走的分支。
   *  track/flush 皆不 throw（tracker 硬規則），不影響主流程；未登入時 tracker
   *  會因無 token 丟棄（401 路徑看後端 log 即可）。 */
  /** 登入回跳開機＋抓不到目的地：目的地只在 LIFF SDK 肚子裡、它會晚一點把網址換過去
   *  （2026-09-03 診斷第三輪）。等它換（100ms 輪詢、上限 3 秒），換了就同步 router 導過去；
   *  逾時（SDK 也沒有目的地＝使用者真的只是開 App）才回首頁。 */
  const waitSdkDeliverOrHome = () => {
    // 等待窗內 authReady 已 true、根路徑會渲染 Welcome 頁（2026-09-09 15:22 使用者實見）——
    // hold 住 splash 撐過等待窗，落點確定（送達或逾時導首頁）後才收。
    holdBootSplash();
    const t0 = Date.now();
    const timer = window.setInterval(() => {
      const p = window.location.pathname;
      if (p !== "/") {
        window.clearInterval(timer);
        trackBootDiag("oauth_callback_delivered", { late_path: p + window.location.search });
        // SDK 用 replaceState 換網址不會驚動 React Router，補一次 navigate 同步
        navigate(p + window.location.search, { replace: true });
        releaseBootSplash();
      } else if (Date.now() - t0 > 3000) {
        window.clearInterval(timer);
        trackBootDiag("oauth_callback_timeout", {});
        navigate("/home", { replace: true });
        releaseBootSplash();
      }
    }, 100);
  };

  const trackBootDiag = (branch: string, extra: Record<string, unknown> = {}) => {
    try {
      track("liff_boot_diag", {
        branch,
        boot_pathname: capturedBootLocation.pathname,
        boot_search: capturedBootLocation.search,
        captured_liff_state: capturedLiffStateAtBoot,
        now_pathname: window.location.pathname,
        now_search: window.location.search,
        ...extra,
      });
      void flush();
    } catch {
      // 診斷不可影響主流程
    }
  };

  /** OA 入口帳戶檢查：已註冊者且本次 URL 帶 ?supplier= 才觸發。
   *  回傳 true = 已導去 onboarding 加帳戶流程（呼叫端不要再做後續導頁）。fail-open：API 失敗照常放行。
   *  effPathname/effSearch：呼叫端解過 liff.state race 的「有效位置」，不在這裡自己重讀
   *  window.location（見 liffStateDeepLink.ts；呼叫端各自的有效位置解析時機不同，統一由外面傳入）。 */
  const runOaEntryCheck = async (
    accessToken: string,
    effPathname: string,
    effSearch: string,
  ): Promise<boolean> => {
    const entry = parseOaEntryFromSearch(effSearch);
    if (!entry || isPayLinkMode() || isSmsInviteOpenMode() || isSmsShortLinkMode() || isPracticeMode()) return false;
    // onboarding 頁自己會判斷是否走加帳戶流程（已註冊＋入口帶 supplier），不在 provider 重複攔（避免迴圈）
    if (/^\/onboarding(\/|$)/.test(effPathname)) return false;
    try {
      const res = await entryCheck({ accessToken, supplier_id: entry.supplierId });
      const data = res?.data?.data as EntryCheckData | undefined;
      if (!data) return false;
      const action = resolveEntryCheckAction(data, entry);
      if (action.kind === "proceed") {
        if (action.preferredContextId != null) {
          sessionStorage.setItem(OA_ENTRY_PREFERRED_CONTEXT_KEY, String(action.preferredContextId));
        }
        clearShopInviteCode(); // 已有帳戶：入口參數用畢即清，修 sessionStorage 殘留
        clearReferredCourierId(); // 同上：已有帳戶不會再走序號註冊，courierId 一併清
        return false;
      }
      // I3（2026-09-02）：導頁前先收 global loading，否則會卡在轉場 loading 直到
      // onboarding 加帳戶頁自己關掉，觀感上像卡住。
      setLoading(false);
      navigate(action.to, { replace: true });
      return true;
    } catch (e) {
      console.error("[入口檢查] entry-check 失敗（fail-open 放行）", e);
      return false;
    }
  };

  //@ 判斷是否有服務登入權限
  const handleAppLogin = (accessToken: string) => {
    const navigateToStoredPath = (): boolean => {
      const callbackPath = sessionStorage.getItem("callbackPathname");
      const currentPath = window.location.pathname + window.location.search;

      if (callbackPath && callbackPath !== currentPath) {
        sessionStorage.removeItem("callbackPathname");
        markBootRedirecting();
        window.location.href = callbackPath;
        return true;
      }
      return false;
    };
    // liff.state race（2026-09-03 實測深連結被誤導首頁）：LIFF SDK 何時把網址換成
    // 深連結目的地不保證早於這裡的判斷，不能直接信 window.location。改用
    // resolveEffectiveLocation 自己解析 ?liff.state= 當唯一真相；沒有 liff.state 時
    // effPath/effSearch 就等於 window.location，以下所有判斷與原行為逐點等價。
    const { pathname: effPath, search: effSearch } = resolveEffectiveLocation(
      window.location.pathname,
      window.location.search,
      capturedLiffStateAtBoot, // SDK 已把 liff.state 從網址清掉時的後備（開機抄下的原始值）
    );
    const isRegister = /^\/(register)(\/|$)/.test(effPath);
    // onboarding（掃 QR / 傳單）是新客戶自己的入口：未註冊者要留在此流程建立客戶，
    // 不可像舊流程那樣被導去 /register 客戶序號頁。
    const isOnboarding = /^\/onboarding(\/|$)/.test(effPath);
    // 簡訊邀請落地頁：未註冊者要留在此頁靜默完成「註冊 + 綁定 + 直達訂單」，
    // 不可像舊流程那樣在 login 401 時被導去 /register 客戶序號頁。
    const isSmsInvite = /^\/sms-invite(\/|$)/.test(effPath);
    // 家庭邀請深連結（/account/addAccount?invite_code=）：未註冊者整段留在 addAccount，
    // 由頁內 JoinRegister（手機驗證 → register-by-invite 靜默註冊＋加入）打通，
    // 不導去任何 /register 頁。
    const isFamilyInviteJoin = /^\/account\/addAccount(\/|$)/.test(effPath);
    // 訪客付款連結（liff.line.me/{liff}/pay/{單}?pt=）：未註冊也能付，不導去註冊。
    const isGuestPayLink = /^\/pay\/[^/]+$/.test(effPath) && (new URLSearchParams(effSearch).get("pt") ?? "").trim() !== "";
    // iPASS MONEY APP 轉導入口：未註冊者要留在此頁拿共享資料去預填註冊，不可導去序號註冊
    const isIPassEntry = /^\/ipass\/entry(\/|$)/.test(effPath);

    handleRequest({
      asyncRequest: () =>
        api.post("/app-api/line/login", {
          accessToken: accessToken,
        }),
      successCallback: async (res: AxiosResponse) => {
        const { jwtToken, redirectUrl } = res.data.data;
        sessionStorage.setItem("jwtToken", jwtToken);

        // /register 註冊流程：不經過 OA 入口檢查（該流程有自己的帳戶檢查），維持原順序。
        if (isRegister) {
          setCusJwtToken(jwtToken);
          setAuthReadyTrue();
          // 已註冊者點瓦斯行序號連結（/register/step2/inviteCode?invite=）：
          // 這裡就是判定「已註冊」的那一點——原本會直接用 redirectUrl 送回 LINE OA
          // chat，序號沒有任何地方接手（死路）。改導去加帳戶的序號輸入頁並帶入預填，
          // 讓使用者按確認送出。只認本次 URL 帶的 invite，殘值不觸發（見該函式註解）。
          const serialLinkTarget = resolveSerialLinkRegisteredRedirect(effPath, effSearch);
          if (serialLinkTarget) {
            trackBootDiag("serial_redirect", { eff_path: effPath, eff_search: effSearch, target: serialLinkTarget });
            setLoading(false);
            navigate(serialLinkTarget, { replace: true });
            return;
          }
          // 用 backend 回的 redirectUrl 送回 LINE OA chat
          if (redirectUrl) {
            trackBootDiag("register_redirect_oa", { eff_path: effPath, eff_search: effSearch });
            setLoading(true);
            markBootRedirecting();
            window.location.href = redirectUrl;
          } else {
            trackBootDiag("register_stay", { eff_path: effPath, eff_search: effSearch });
            setLoading(false);
          }
          return;
        }

        // I2（2026-09-02）：setCusJwtToken/setAuthReadyTrue 挪到 runOaEntryCheck 之後——
        // proceed 分支會先寫入 OA_ENTRY_PREFERRED_CONTEXT_KEY 偏好身分 key，必須早於
        // setCusJwtToken 觸發的 IdentitySwitchMenu contexts fetch，否則那次 fetch 抓不到
        // 剛寫入的偏好 key，會切錨錯身分（race）。entry-check 失敗會 fail-open（回 false），
        // jwt 仍照常在下面設定、authReady 照常 true，不會因此擋掉整個登入。
        const redirectedByEntryCheck = await runOaEntryCheck(accessToken, effPath, effSearch);
        setCusJwtToken(jwtToken);
        setAuthReadyTrue();
        if (redirectedByEntryCheck) {
          trackBootDiag("entry_check_redirect", { eff_path: effPath, eff_search: effSearch });
          return;
        }

        // OAuth 完成後若停在根路徑（Welcome splash），導去首頁；
        // 深連結（/orders /account 等）不動，避免把使用者從目標頁踢走
        const goHome = effPath === "/" || effPath === "";
        const onHome = effPath === "/home" || effPath.startsWith("/home/");
        if (goHome) {
          if (capturedLiffStateAtBoot === null && looksLikeLiffOauthCallbackBoot(window.location.search)) {
            // 登入回跳且前端從未見過目的地 → 等 SDK 送達，不急著回首頁
            trackBootDiag("oauth_callback_wait", { eff_path: effPath, eff_search: effSearch });
            waitSdkDeliverOrHome();
          } else {
            trackBootDiag("go_home", { eff_path: effPath, eff_search: effSearch });
            navigate("/home", { replace: true });
          }
        } else if (window.location.pathname === "/") {
          // effPath 非 "/"，但實際網址現在仍是 "/"：LIFF SDK 還沒把網址換成深連結
          // 目的地（liff.state race）。不等 SDK，自己主動送達目的地——React Router
          // 的 navigate 與之後 SDK 的 replaceState 相容，SDK 晚一步套用只是把網址
          // 改成同一個值，等於 no-op。
          trackBootDiag("deliver_deeplink", { eff_path: effPath, eff_search: effSearch });
          navigate(effPath + effSearch, { replace: true });
        } else {
          trackBootDiag("stay", { eff_path: effPath, eff_search: effSearch });
        }

        // 進首頁時不要在這裡關 loading：
        // setCusJwtToken 後 HomePage layout 會先 setLoading(true) 等 /home，
        // 若此處再 false，會把那次 true 蓋掉 → 之後 fetch 又 true，鋼瓶跳第二次。
        // 有快取時 HomePage 會自己 setLoading(false)；無快取則等 /home 回來再關。
        if (!goHome && !onHome) {
          setLoading(false);
        }
      },
      errorCallback: (error: any) => {
        // 登入失敗一律關 loading
        setLoading(false);
        if (error.response?.status === 401) {
          // 401＝此 LINE 帳號未註冊：清掉殘留的舊 jwt（sessionStorage 與 zustand persist
          // 是兩份），否則上次測試留下的 jwt 會讓頁面誤判為已註冊，行為時好時壞。
          sessionStorage.removeItem("jwtToken");
          setCusJwtToken(null);
          // 0. 訪客付款連結：未註冊照樣留在付款頁付款（排在瓦斯行邀請碼導流之前，
          //    否則同分頁殘留的 shopCode 會把付款的人帶去註冊）。目的地還停在根路徑
          //    （liff.state 還沒被 SDK 套用）就自己送達。
          if (isGuestPayLink) {
            if (window.location.pathname === "/") {
              trackBootDiag("guest_pay_deliver", { eff_path: effPath });
              navigate(effPath + effSearch, { replace: true });
            }
            setAuthReadyTrue();
            return;
          }
          // 1. 帶著瓦斯行邀請碼進站的未註冊者（例如從瓦斯行 OA 的 rich menu 點進來）
          //    → 直接進該瓦斯行的傳單註冊流程。必須排在 navigateToStoredPath() 之前，
          //    否則會先被 callbackPathname 送回 /kiosk 而永遠走不到這裡。
          //    注意：這裡判斷的是 getShopInviteCode()／getShopEntrySupplierId() 讀出的
          //    sessionStorage 值，不是「這次觸發 401 的當下 URL 有沒有帶 shopCode」。
          //    resolveShopEntryRedirect() 要求 shopCode 與 supplierId 同時存在才會導流：
          //    兩者一定是同一個 OA 深連結成對寫入的，只要有一個缺席，代表這不是本次從
          //    OA 進站帶來的值，而是舊連結留在同一分頁 sessionStorage 裡的殘渣，此時會
          //    忽略它、回 null，繼續往下走既有的深連結／序號註冊導流。
          //    clearShopInviteCode() 除了序號註冊完成（Register/pages/profile）會呼叫，
          //    onboarding 完成（finish / finishExistingPending / goNotFoundHome 系列）與本檔
          //    OA 入口檢查 proceed 分支（見上方 runOaEntryCheck）也會清（F4，2026-09-02）。
          const shopRedirect = resolveShopEntryRedirect({
            pathname: effPath,
            shopCode: getShopInviteCode(),
            supplierId: getShopEntrySupplierId(),
          });
          if (shopRedirect) {
            sessionStorage.removeItem("callbackPathname");
            navigate(shopRedirect, { replace: true });
            setAuthReadyTrue();
            return;
          }
          // 2. 有深連結（callbackPathname）優先回深連結 — navigateToStoredPath()
          //    成功時做 window.location.href，後面不需要再 navigate。
          // 3. 沒有深連結且不是註冊流程 → 帶到 register/inviteCode。
          // 4. 註冊流程本身 401 → 不做事，讓上層 UI 處理。
          const wentToStored = navigateToStoredPath();
          if (!wentToStored && (isRegister || isOnboarding || isSmsInvite || isFamilyInviteJoin || isIPassEntry) && effPath !== window.location.pathname && window.location.pathname === "/") {
            // 4a. 目的地是靠 liff.state 解析／備援救回的（effPath ≠ 實際網址、實際還停在根路徑）：
            //     已註冊分支有 deliver_deeplink 主動送達，未註冊者也要送，否則會停在歡迎頁。
            trackBootDiag("deliver_deeplink_401", { eff_path: effPath, eff_search: effSearch });
            navigate(effPath + effSearch, { replace: true });
          } else if (!wentToStored && !isRegister && !isOnboarding && !isSmsInvite && !isFamilyInviteJoin && !isIPassEntry) {
            // 帶家庭邀請碼（掃 QR）的未註冊者 → 走零表單邀請註冊；否則走客戶序號註冊。
            navigate(getFamilyInviteCode() ? "/register/join" : "/register/step2/inviteCode");
          }
          // onboarding 未註冊者：留在原 /onboarding 入口，由 onboarding 流程自行建立新客戶。
          // addAccount 家庭邀請深連結：留在原頁，由頁內 JoinRegister 完成註冊＋加入。
        }
        setAuthReadyTrue();
      },
      finalCallback: () => {
        setAuthReadyTrue();
      },
    });
  };
  useEffect(() => {
    // 最先攔截：把網址上的家庭邀請碼存起來，跨 LINE login redirect 與 register 導頁保留，
    // 供「未註冊 → 註冊後立即加入家庭」(scenario 1) 使用。
    captureFamilyInviteFromUrl();
    // 同時攔截瓦斯行邀請碼（shopCode），跨流程攜帶至註冊送出時驗證入口瓦斯行與 CIS 推導是否一致。
    captureShopInviteFromUrl();
    // 攔截 DAPP 序號連結帶的配送員 id（courierId），註冊送出時帶給後端做「誰引導註冊」歸因備援。
    captureReferredCourierFromUrl();
    // 攔截簡訊邀請參數（phone / invite / k / order_id），跨 LINE login redirect 保留，
    // 供 /sms-invite 落地頁靜默完成「註冊 + 綁定 + 直達訂單詳情」。
    captureSmsInviteFromUrl();
    // 未註冊掃付款 QR（/pay/{訂單}?invite_code=）：記下付款頁 returnTo，供 joinByInvite
    // 註冊並加入家庭後導回付款頁（而非帳戶頁）。此時已在登入後、query 已由
    // shouldPreserveQueryOnLogin 保留，之後導往註冊流程不再有 login redirect，sessionStorage 可存活。
    if (
      /^\/pay\//.test(window.location.pathname) &&
      (new URLSearchParams(window.location.search).get("invite_code") ?? "").trim() !== ""
    ) {
      setPayReturnTo(window.location.pathname);
    }

    const callbackPath = sessionStorage.getItem("callbackPathname") || "";

    if (isInitialized.current) return;
    isInitialized.current = true;

    // 客服練習模式：完全跳過 LIFF，注入假 token，讓四步驟畫面可離線操作。
    // 旗標關閉時（正式客戶）不進此分支，LIFF 流程與原本一致。
    if (isPracticeMode()) {
      sessionStorage.setItem("lineAccessToken", PRACTICE_FAKE_LINE_TOKEN);
      setLineAccessToken(PRACTICE_FAKE_LINE_TOKEN);
      sessionStorage.setItem("jwtToken", PRACTICE_FAKE_JWT);
      setCusJwtToken(PRACTICE_FAKE_JWT);
      // 練習專用部署保底：直接開 /home、重新整理、rich menu 深連結等未經情境種子的情況，
      // 補種預設身分，讓頁面直接有 mock 資料（註冊/綁定/掃碼情境會維持登出，不受影響）。
      ensurePracticeIdentity();
      setLoading(false);
      setAuthReadyTrue();
      return;
    }

    // 從 LINE 登入回來時若落在 "/"，且先前存有深連結路徑，直接導回該頁（不顯示 Welcome）
    const currentPath = window.location.pathname + window.location.search;
    if (callbackPath && (currentPath === "/" || currentPath === "")) {
      sessionStorage.removeItem("callbackPathname");
      markBootRedirecting();
      window.location.href = callbackPath;
      return;
    }

    // 一次性付款連結直接開（不是 liff.line.me，例如 LINE 外的相機、Safari）：不初始化 LIFF、
    // 不導登入，以訪客身分付款。信用卡 3D 驗證回來的落地頁同理（它只負責導回付款連結頁）。
    if (isPayLinkMode() || window.location.pathname === THREE_DS_RESULT_PATH) {
      setLoading(false);
      setAuthReadyTrue();
      return;
    }

    // 簡訊邀請中繼頁（/sms-invite/open，簡訊點開先落在外部瀏覽器）：只負責把客戶帶進
    // 瓦斯行自己的 LIFF（liff.line.me/{liffId}/sms-invite），本頁不初始化 LIFF、不導登入。
    if (isSmsInviteOpenMode()) {
      setLoading(false);
      setAuthReadyTrue();
      return;
    }

    // iPASS MONEY APP 轉導入口（/ipass/entry）不在 LINE 內時：同上，只負責把客戶帶進
    // liff.line.me/{liffId}/ipass/entry，不初始化 LIFF、不導登入——否則外部登入會在
    // 瀏覽器與 LINE 之間無限來回（見 ipassEntryRelay.ts）。
    if (isIPassEntryRelayMode()) {
      setLoading(false);
      setAuthReadyTrue();
      return;
    }

    // 簡訊邀請短連結（/s/{token}）：與上面中繼頁同理，本頁自己拿 token 換參數、
    // 自己決定去 liff.line.me 還是本站 /sms-invite，不初始化 LIFF、不導登入。
    if (isSmsShortLinkMode()) {
      setLoading(false);
      setAuthReadyTrue();
      return;
    }

    // 開發環境：可選擇略過 LIFF 流程，直接注入測試用 jwtToken
    if (isDevSkipLiff) {
      const mockJwt = process.env.REACT_APP_MOCK_JWT || "";
      if (mockJwt && typeof window !== "undefined") {
        sessionStorage.setItem("jwtToken", mockJwt);
        // 與正式流程一致：把 jwtToken 寫進 useCusAuth store，否則讀 cusJwtToken 的頁面（GasShop / Kiosk）拿不到 token
        setCusJwtToken(mockJwt);
      }
      setLoading(false);
      setAuthReadyTrue();
      // 正式流程登入後會導去 /home；dev-skip 也比照，避免卡在 Welcome 啟動頁。
      // 只在 Welcome / login 導頁，保留深連結（例如直接開 /kiosk）。
      const p = window.location.pathname;
      if (p === "/" || p === "/login") navigate("/home", { replace: true });
      return;
    }

    setLoading(true);
    const runtimeLiffId = getRuntimeLiffId();
    if (!runtimeLiffId) {
      console.error("LIFF ID 未設定，請檢查 REACT_APP_LINEID 或 REACT_APP_LINE_OA_MAP");
      setLoading(false);
      setAuthReadyTrue();
      return;
    }

    // 旅程章：liff.init 途中可能被 SDK 自動登入抓走（整頁跳轉、下面的程式全都不會跑）。
    // 先蓋章＝「若被抓走，本頁開機寫的備援就是回跳目的地」；init 平安完成、確定留在
    // 本頁後撤章。本頁沒寫過備援（單純開首頁）則蓋章是 no-op，舊殘值永遠無章。
    stampLoginJourney();
    liff
      .init({
        liffId: runtimeLiffId,
      })
      .then(async () => {
        if (!liff.isLoggedIn()) {
          sessionStorage.setItem("callbackPathname", window.location.pathname + window.location.search);
          // 不帶 redirectUri 時，LINE 登入完會回到 LIFF 端點網址（根路徑），query 全部
          // 掉光，只能靠 sessionStorage——但實測在 LINE webview 的 login round-trip 後
          // 那份資料讀不到，客戶因此被丟去序號註冊頁。明確帶回目前完整網址（含 query），
          // 讓參數自己活過 round-trip，不依賴 storage。
          //   - /sms-invite：phone / invite / k / order_id
          //   - 帶 shopCode 的入口（傳單連結、瓦斯行 OA rich menu）：supplier / shopCode
          markBootRedirecting();
          // 這裡要保住的是「當下網址」給 LINE login 用（帶去 redirectUri，讓參數活過
          // round-trip），與別處判斷目的地的語意不同，維持用真實 window.location——
          // 此時尚未登入，也還沒有從此次登入落地的 liff.state 可解析。
          if (shouldPreserveQueryOnLogin(window.location.pathname, window.location.search)) {
            liff.login({ redirectUri: window.location.href });
          } else {
            liff.login();
          }
        } else {
          // init 平安完成且已登入＝沒被抓去登入、確定留在本頁 → 撤章。
          // （未登入分支不撤：接下來的 liff.login 就是旅程的下一段。）
          revokeLoginJourney();
          const assessToken = liff.getAccessToken();
          sessionStorage.setItem("lineAccessToken", assessToken || "");
          setLineAccessToken(assessToken);
          // 已有 jwt 且非註冊流程：略過 /line/login，減少每次 rich menu 多打一輪＋少一次 loading 接縫。
          // 註冊流程仍一律重打（與既有行為一致）。
          const existingJwt = sessionStorage.getItem("jwtToken");
          // liff.state race（見 liffStateDeepLink.ts）：與 handleAppLogin 相同做法，
          // 自己解析有效位置，不直接信 window.location 當下狀態。
          const { pathname: effPath, search: effSearch } = resolveEffectiveLocation(
            window.location.pathname,
            window.location.search,
            capturedLiffStateAtBoot, // 同上：SDK 清掉 liff.state 窗口的後備
          );
          const isRegisterPath = /^\/(register)(\/|$)/.test(effPath);
          if (existingJwt && !isRegisterPath) {
            // I2（2026-09-02）：setCusJwtToken/setAuthReadyTrue 挪到 runOaEntryCheck 之後，
            // 理由同 handleAppLogin successCallback（偏好身分 key 要早於 contexts fetch 寫入）。
            const redirectedByEntryCheck = await runOaEntryCheck(assessToken || "", effPath, effSearch);
            setCusJwtToken(existingJwt);
            setAuthReadyTrue();
            if (redirectedByEntryCheck) {
              trackBootDiag("fast_entry_check_redirect", { eff_path: effPath, eff_search: effSearch });
              return;
            }
            if (effPath === "/" || effPath === "") {
              if (capturedLiffStateAtBoot === null && looksLikeLiffOauthCallbackBoot(window.location.search)) {
                trackBootDiag("fast_oauth_callback_wait", { eff_path: effPath, eff_search: effSearch });
                waitSdkDeliverOrHome();
              } else {
                // 停在 Welcome 根路徑時導去首頁；維持 loading 交給 HomePage
                trackBootDiag("fast_go_home", { eff_path: effPath, eff_search: effSearch });
                navigate("/home", { replace: true });
              }
            } else if (window.location.pathname === "/") {
              // effPath 非 "/"，但實際網址現在仍是 "/"：SDK 還沒套用（race）。
              // 不等 SDK，自己主動送達目的地；SDK 之後才套用等於 no-op。
              trackBootDiag("fast_deliver_deeplink", { eff_path: effPath, eff_search: effSearch });
              navigate(effPath + effSearch, { replace: true });
            } else if (!effPath.startsWith("/home")) {
              // kiosk 等頁面有自己的頁內 spinner／authReady 清 loading
              trackBootDiag("fast_stay", { eff_path: effPath, eff_search: effSearch });
              setLoading(false);
            } else {
              trackBootDiag("fast_home_keep_loading", { eff_path: effPath, eff_search: effSearch });
            }
            // /home：維持 loading，等 HomePage 快取或 /home API
            return;
          }
          handleAppLogin(assessToken || "");
        }
      })
      .catch((err) => {
        console.error("LIFF 初始化失敗", err);
        setLoading(false);
        setAuthReadyTrue();
      });
  }, [setLoading]);

  // 授權塵埃落定＝確定停在這一頁 → 收掉開機 splash。
  // 還要導走的情況已由 markBootRedirecting() 擋住，splash 會撐到下一個 document 接手。
  useEffect(() => {
    if (authReady) hideBootSplash();
  }, [authReady]);

  return <AuthReadyContext.Provider value={authReady}>{children}</AuthReadyContext.Provider>;
};

export default LineLoginProvider;
